import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User } from '@entities/user/types';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
}

export const STORAGE_KEY = 'osnova.auth';

// MVP: tokens are stored in localStorage. In production we should move the
// refresh token to a httpOnly cookie set by the backend to mitigate XSS exfiltration.
export function readPersisted(): Pick<AuthState, 'accessToken' | 'refreshToken'> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { accessToken: null, refreshToken: null };
    const parsed = JSON.parse(raw) as {
      accessToken?: string;
      refreshToken?: string;
    };
    return {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

function persist(state: Pick<AuthState, 'accessToken' | 'refreshToken'>) {
  if (!state.accessToken && !state.refreshToken) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const initialState: AuthState = {
  ...readPersisted(),
  user: null,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      persist(state);
    },
    setUser(state, action: PayloadAction<User | null>) {
      state.user = action.payload;
    },
    clearTokens(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      persist(state);
    },
  },
});

export const { setTokens, setUser, clearTokens } = slice.actions;
export default slice.reducer;
